/*
  Glimmery cart add-on (snippets/g-cart-upsell.liquid) in the cart drawer body and on the cart page.
  Nothing is pre-selected. Until a size is chosen "Add" is aria-disabled (still focusable): activating it explains
  why ("Choose your size first.") and moves focus to the size select, without any request.
  Adds exactly the chosen variant through the shared cart queue (window.GlimmeryCart, assets/cart.js):
  - in the drawer it re-renders the drawer from the server response (cart-drawer.js announces the add once);
  - on the cart page it refreshes the cart page sections through its line controller (one live announcement).
  Idempotent: the element is defined once; every re-render creates fresh elements.
*/
(() => {
  if (customElements.get('g-cart-upsell')) return;

  const DRAWER_SECTIONS = ['cart-drawer', 'cart-icon-bubble', 'cart-live-region-text'];

  class GCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.busy = false;
      this.field = this.querySelector('[data-g-upsell-variant]');
      this.button = this.querySelector('[data-g-upsell-add]');
      this.status = this.querySelector('.g-upsell__status');
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.field?.addEventListener('change', this.onChange);
      this.button?.addEventListener('click', this.onClick);
      this.sync();
    }

    disconnectedCallback() {
      this.field?.removeEventListener('change', this.onChange);
      this.button?.removeEventListener('click', this.onClick);
      this.ready = false;
    }

    get variantId() {
      return this.field && this.field.value ? String(this.field.value) : '';
    }

    // aria-disabled, never `disabled`: a disabled button drops keyboard focus while it waits for the server.
    sync() {
      if (!this.button) return;
      this.button.removeAttribute('disabled');
      if (this.busy || !this.variantId) this.button.setAttribute('aria-disabled', 'true');
      else this.button.removeAttribute('aria-disabled');
      this.classList.toggle('is-ready', Boolean(this.variantId));
    }

    onChange() {
      this.setStatus('');
      this.sync();
    }

    setStatus(text, isError) {
      if (!this.status) return;
      this.status.textContent = text || '';
      this.status.classList.toggle('is-error', Boolean(isError && text));
    }

    setBusy(busy) {
      this.busy = busy;
      if (this.button) {
        if (busy) this.button.setAttribute('aria-busy', 'true');
        else this.button.removeAttribute('aria-busy');
      }
      if (this.field && this.field.tagName === 'SELECT') this.field.disabled = busy;
      this.sync();
    }

    async onClick(event) {
      event.preventDefault();
      if (this.busy) return;
      const id = this.variantId;
      if (!id) {
        // No request without a size: say why and lead to the size select.
        this.setStatus(this.dataset.needSize || '', true);
        if (this.field && typeof this.field.focus === 'function') this.field.focus();
        return;
      }
      const cart = window.GlimmeryCart;
      const routes = window.routes || {};
      if (!cart || !routes.cart_add_url) return;

      const drawer = this.closest('cart-drawer');
      const page = drawer ? null : this.closest('cart-items');

      this.setBusy(true);
      this.setStatus('');
      const deferred = this.linesUpdateEvent(id, drawer ? 'dialog' : 'cart');
      const body = JSON.stringify(
        Object.assign(
          { items: [{ id: Number(id), quantity: 1 }] },
          drawer ? { sections: DRAWER_SECTIONS, sections_url: window.location.pathname } : {}
        )
      );
      const result = await cart.enqueue(() =>
        cart.request(`${routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body,
        })
      );
      const data = result && result.data;
      const ok = result && result.ok && data && !data.status && Array.isArray(data.items);

      if (!ok) {
        const message = cart.errorMessage(result, this.dataset.error || '');
        deferred?.reject(new Error(message));
        this.setBusy(false);
        this.setStatus(message, true);
        return;
      }

      this.resolveLinesUpdate(deferred);

      if (drawer) {
        let rendered = false;
        if (typeof drawer.renderContents === 'function' && cart.hasSections(data.sections, ['cart-drawer'])) {
          try {
            // Renders, highlights the new line and announces it once, named like an add from the product page:
            // "Added to cart: {name}, Front print, size M. Cart updated: …" (the line is found by data.items[0].key).
            drawer.renderContents({ sections: data.sections, items: data.items });
            rendered = true;
            this.focusLine(drawer, id);
          } catch (error) {
            console.error(error);
          }
        }
        if (!rendered && drawer.lines) drawer.lines.refresh();
        this.publish(id, 'cart-drawer-items', data);
      } else if (page && page.lines) {
        // The cart page re-renders its own sections (lines, this add-on, summary) and announces the new bag.
        // Published as 'cart-items' so the cart page does not refresh a second time.
        this.publish(id, 'cart-items', data);
        await page.lines.refresh({ addedVariantId: id });
        this.focusLine(page, id);
      } else {
        this.publish(id, 'cart-page-upsell', data);
      }

      if (this.isConnected) {
        this.setBusy(false);
        this.setStatus(this.dataset.added || '');
      }
    }

    // Focus the new line's title (this add-on is replaced by the next suggestion).
    focusLine(root, id) {
      const name = root.querySelector(`.g-cart-line[data-variant-id="${id}"] [data-g-control="name"]`);
      if (name) name.focus();
    }

    publish(id, source, cartData) {
      try {
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source, productVariantId: id, cartData });
        }
      } catch (error) {
        console.error(error);
      }
    }

    linesUpdateEvent(id, context) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!CartLinesUpdateEvent) return null;
      try {
        const deferred = CartLinesUpdateEvent.createPromise();
        deferred.promise.catch(() => {});
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context,
            lines: [{ merchandiseId: id, quantity: 1 }],
            promise: deferred.promise,
          })
        );
        return deferred;
      } catch (e) {
        return null;
      }
    }

    resolveLinesUpdate(deferred) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!deferred || !CartLinesUpdateEvent) return;
      fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((cartData) => {
          if (!cartData || !cartData.currency) throw new Error('Missing cart');
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartData) });
        })
        .catch((e) => deferred.reject(e));
    }
  }

  customElements.define('g-cart-upsell', GCartUpsell);
})();
